import { useState } from 'react'
import { HorizontalBarChart } from '../components'
import { useDocumentTitle } from '@/shared/hooks/use-document-title'
// Distribución real por especialidad (BD Colombia). Sin ciudad/provincia — solo especialidad.
import especialidadesData from '../origenes/tonal-colombia.json'

const ESPECIALIDADES_SIM: { nombre: string; total: number }[] = especialidadesData

const TOTAL = ESPECIALIDADES_SIM.reduce((a, e) => a + e.total, 0) // 53.027

export function TonalColombiaBdPage() {
  useDocumentTitle('Base de Datos Médicos · Colombia')
  const [espOpen, setEspOpen] = useState(false)

  const especialidades = ESPECIALIDADES_SIM.map((e) => ({
    ...e,
    porcentaje: Math.round((e.total / TOTAL) * 1000) / 10,
  }))

  function gradientColors(items: { total: number }[], hex: string, minAlpha = 0.25): string[] {
    const max = Math.max(...items.map((i) => i.total), 1)
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return items.map((item) => {
      const alpha = minAlpha + (1 - minAlpha) * (item.total / max)
      return `rgba(${r},${g},${b},${alpha.toFixed(2)})`
    })
  }

  const espColors = gradientColors(especialidades, '#57a6d8')
  const chartEsp = especialidades.map((e, i) => ({
    label: e.nombre,
    value: e.total,
    color: espColors[i],
  }))

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-5xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">Base de Datos Tonal</h1>
                <p className="text-sm lg:text-base text-gray-600">Médicos Colombia — distribución por especialidad</p>
              </div>
            </div>
            <a
              href="https://enterprise.tonalstrategies.com/pricing/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              Calculador
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        {/* Estadísticas Generales */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 lg:p-6 border border-blue-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-[#57a6d8] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen General
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{TOTAL.toLocaleString('es-CO')}</p>
              <p className="text-sm text-gray-600 mt-1">Total Médicos</p>
            </div>
            {especialidades.slice(0, 3).map((esp, i) => (
              <div key={esp.nombre} className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
                <p className="text-3xl font-bold" style={{ color: espColors[i] }}>
                  {esp.total.toLocaleString('es-CO')}
                </p>
                <p className="text-sm text-gray-600 mt-1">{esp.nombre}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Gráfica por Especialidad */}
        <div className="grid grid-cols-1 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
              <svg className="w-5 h-5 text-[#57a6d8] mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
              Distribución por Especialidad
            </h3>
            <HorizontalBarChart data={chartEsp} />
            <p className="text-xs text-gray-400 mt-3 italic">
              Distribución por especialidad · {TOTAL.toLocaleString('es-CO')} médicos · {ESPECIALIDADES_SIM.length} especialidades.
            </p>
          </div>
        </div>

        {/* Desglose por Especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setEspOpen((o) => !o)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-[#57a6d8] transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-[#57a6d8] mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                <span className="text-base lg:text-lg">Desglose por Especialidad</span>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${espOpen ? 'rotate-180' : ''}`}
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {espOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left font-semibold text-gray-700">Especialidad</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">Registros</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 font-semibold text-gray-700">Distribución</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {especialidades.map((esp) => (
                    <tr key={esp.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{esp.nombre}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{esp.total.toLocaleString('es-CO')}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-[#57a6d8]">{esp.porcentaje}%</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="h-2.5 rounded-full" style={{ background: '#57a6d8', width: `${esp.porcentaje}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{TOTAL.toLocaleString('es-CO')}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-[#57a6d8]">100%</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies — Base de Datos Médicos Colombia</p>
        </div>

      </div>
    </div>
  )
}
